import api, { auth } from './axios';
import {
  Client,
  Fournisseur,
  Article,
  Document,
  Paiement,
  Company,
  DashboardStats,
  Page,
  ClientSearchParams,
  ScoreClient,
  ArticleSearchParams,
  Produit360,
  ComparateurFournisseur,
  DocumentSearchParams,
  PaiementSearchParams,
  RapportTVAData,
  EtatAnnuelData,
  MargeRapport,
  Opportunite,
  Pipeline,
  Contact,
  Alerte,
  BalanceAgee,
  Tresorerie,
  CockpitCarbone,
  FactureElectroniqueStatus,
  InvoiceTemplate,
  NumberingConfig,
  Echeancier,
} from '../types';

/**
 * The backend now returns plain arrays (no Spring pagination). The list pages were written
 * against `Page<T>` — we wrap the arrays here so they keep working without touching the pages.
 * `params.page` / `params.size` are still honoured client-side for simple pagination.
 */
function paginateLocal<T>(all: T[], params?: { page?: number; size?: number }): Page<T> {
  const items = Array.isArray(all) ? all : [];
  const size = params?.size ?? 10;
  const page = params?.page ?? 0;
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / size));
  const start = page * size;
  return {
    content: items.slice(start, start + size),
    totalElements: total,
    totalPages,
    size,
    number: page,
    first: page === 0,
    last: page >= totalPages - 1,
  };
}

function applyTextFilter<T extends Record<string, any>>(items: T[], needle?: string, keys: string[] = []): T[] {
  if (!needle) return items;
  const q = needle.toLowerCase();
  return items.filter((it) =>
    keys.some((k) => {
      const v = it[k];
      return typeof v === 'string' && v.toLowerCase().includes(q);
    }),
  );
}

// Dashboard
export const dashboardService = {
  getStats: () => api.get<DashboardStats>('/dashboard/stats').then((r) => r.data),
};

// Clients
export const clientService = {
  getAll: async (params?: ClientSearchParams & { page?: number; size?: number }) => {
    const all = (await api.get<Client[]>('/clients')).data;
    let filtered: Client[] = all;
    if (params?.search)
      filtered = applyTextFilter(filtered, params.search, ['raisonSociale', 'email', 'matriculeFiscal']);
    if (params?.categorie) filtered = filtered.filter((c: any) => c.categorieClient === params.categorie);
    if (params?.actif !== undefined) filtered = filtered.filter((c: any) => c.actif === params.actif);
    return paginateLocal(filtered, params);
  },

  getById: (id: number) => api.get<Client>(`/clients/${id}`).then((r) => r.data),

  create: (client: Partial<Client>) => api.post<Client>('/clients', client).then((r) => r.data),

  update: (id: number, client: Partial<Client>) =>
    api.put<Client>(`/clients/${id}`, client).then((r) => r.data),

  delete: (id: number) => api.delete(`/clients/${id}`).then((r) => r.data),

  search: (query: string) =>
    api.get<Client[]>('/clients/search', { params: { query } }).then((r) => r.data),

  // TF-018 — score de valeur/risque du client
  getScore: (id: number) =>
    api.get<ScoreClient>(`/clients/${id}/score`).then((r) => r.data),
  // TF-079 — segmentation RFM + recommandations (next-best-offer)
  getRecommandations: (id: number) =>
    api.get<import('../types').RecommandationClient>(`/clients/${id}/recommandations`).then((r) => r.data),
};

// Fournisseurs (tiers d'achat) — distincts des clients
export const fournisseurService = {
  getAll: async (params?: { search?: string; page?: number; size?: number }) => {
    const all = (await api.get<Fournisseur[]>('/fournisseurs')).data;
    let filtered: Fournisseur[] = all ?? [];
    if (params?.search)
      filtered = applyTextFilter(filtered, params.search, ['raisonSociale', 'email', 'matriculeFiscal']);
    return paginateLocal(filtered, params);
  },
  getById: (id: number) => api.get<Fournisseur>(`/fournisseurs/${id}`).then((r) => r.data),
  articles: (id: number) =>
    api.get<ArticleFournisseur[]>(`/fournisseurs/${id}/articles`).then((r) => r.data),
  create: (data: Partial<Fournisseur>) =>
    api.post<Fournisseur>('/fournisseurs', data).then((r) => r.data),
  update: (id: number, data: Partial<Fournisseur>) =>
    api.put<Fournisseur>(`/fournisseurs/${id}`, data).then((r) => r.data),
  delete: (id: number) => api.delete(`/fournisseurs/${id}`).then((r) => r.data),
  search: (query: string) =>
    api.get<Fournisseur[]>('/fournisseurs/search', { params: { query } }).then((r) => r.data),
};

// Articles
/**
 * The frontend Article type and ArticleForm use display-friendly names
 * (`prixUnitaireHT`, `fodec`, `actif`, `code`) while the backend DTO uses
 * (`prixUnitaire`, `fodecApplicable`, `active`, `reference`). Without this
 * adapter, edits silently fail: the backend rejects `prixUnitaire=null`
 * (@NotNull + NOT NULL DB column) and the form never re-populates after a
 * successful GET (response field names don't match the form's register paths).
 */
function toBackendArticle(a: Partial<Article>): any {
  if (!a) return a;
  const out: any = { ...a };
  if ('prixUnitaireHT' in out) { out.prixUnitaire = out.prixUnitaireHT; delete out.prixUnitaireHT; }
  if ('fodec' in out) { out.fodecApplicable = Boolean(out.fodec); delete out.fodec; }
  if ('actif' in out) { out.active = Boolean(out.actif); delete out.actif; }
  // Backend has a single `reference` column — the form exposes both `code` and
  // `reference`. `code` is the primary identifier shown everywhere else in the UI
  // (ArticlePicker, lists), so it wins when both are present.
  if (out.code) out.reference = out.code;
  delete out.code;
  return out;
}

function fromBackendArticle(a: any): Article {
  if (!a) return a;
  return {
    ...a,
    code: a.reference,
    prixUnitaireHT: a.prixUnitaire,
    fodec: Boolean(a.fodecApplicable),
    actif: Boolean(a.active),
  };
}

export const articleService = {
  getAll: async (params?: ArticleSearchParams & { page?: number; size?: number }) => {
    const raw = (await api.get<any[]>('/articles')).data;
    const all: Article[] = (raw ?? []).map(fromBackendArticle);
    let filtered: Article[] = all;
    if (params?.search)
      filtered = applyTextFilter(filtered, params.search, ['reference', 'designation', 'categorie']);
    if ((params as any)?.tauxTva) filtered = filtered.filter((a: any) => a.tauxTva === (params as any).tauxTva);
    // The form/list models the active flag as `actif`; accept either filter key.
    const actifFilter = (params as any)?.actif ?? (params as any)?.active;
    if (actifFilter !== undefined) filtered = filtered.filter((a: any) => Boolean(a.actif) === Boolean(actifFilter));
    return paginateLocal(filtered, params);
  },

  getById: (id: number) => api.get<any>(`/articles/${id}`).then((r) => fromBackendArticle(r.data)),

  create: (article: Partial<Article>) =>
    api.post<any>('/articles', toBackendArticle(article)).then((r) => fromBackendArticle(r.data)),

  quickCreate: (designation: string) =>
    api.post<any>('/articles/quick', { designation }).then((r) => fromBackendArticle(r.data)),

  update: (id: number, article: Partial<Article>) =>
    api.put<any>(`/articles/${id}`, toBackendArticle(article)).then((r) => fromBackendArticle(r.data)),

  delete: (id: number) => api.delete(`/articles/${id}`).then((r) => r.data),

  search: (query: string) =>
    api.get<any[]>('/articles/search', { params: { query } }).then((r) => (r.data ?? []).map(fromBackendArticle)),

  // TF-016 — fiche produit 360°
  getFiche360: (id: number) =>
    api.get<Produit360>(`/articles/${id}/360`).then((r) => r.data),

  // TF-020 — comparateur fournisseurs pour un article
  getComparateurFournisseurs: (id: number) =>
    api.get<ComparateurFournisseur>(`/articles/${id}/comparateur-fournisseurs`).then((r) => r.data),
};

// TF-076 — variantes/déclinaisons + nomenclature (BOM) + assemblage
export interface VarianteView {
  id: number; reference: string; designation: string; attributs: string;
  prixUnitaire: number; stockActuel: number; active: boolean;
}
export interface NomenclatureLigneView {
  id: number; composantId: number; composantReference: string;
  composantDesignation: string; quantite: number; cmupComposant: number;
}
export interface AssemblageResult {
  articleId: number; quantiteProduite: number; coutUnitaireProduction: number; composantsConsommes: number;
}
export const nomenclatureService = {
  variantes: (articleId: number) =>
    api.get<VarianteView[]>(`/articles/${articleId}/variantes`).then((r) => r.data),
  createVariante: (articleId: number, payload: { attributs: string; reference?: string; prixUnitaire?: number }) =>
    api.post<VarianteView>(`/articles/${articleId}/variantes`, payload).then((r) => r.data),
  nomenclature: (articleId: number) =>
    api.get<NomenclatureLigneView[]>(`/articles/${articleId}/nomenclature`).then((r) => r.data),
  addComposant: (articleId: number, payload: { composantId: number; quantite: number }) =>
    api.post<NomenclatureLigneView>(`/articles/${articleId}/nomenclature`, payload).then((r) => r.data),
  removeComposant: (articleId: number, ligneId: number) =>
    api.delete(`/articles/${articleId}/nomenclature/${ligneId}`).then((r) => r.data),
  assembler: (articleId: number, payload: { quantite: number; depotId?: number | null; numeroLot?: string }) =>
    api.post<AssemblageResult>(`/articles/${articleId}/assembler`, payload).then((r) => r.data),
};

// Invoice templates
export const invoiceTemplateService = {
  getAll: () => api.get<InvoiceTemplate[]>('/invoice-templates').then((r) => r.data),
  getById: (id: number) => api.get<InvoiceTemplate>(`/invoice-templates/${id}`).then((r) => r.data),
  create: (data: Partial<InvoiceTemplate>) =>
    api.post<InvoiceTemplate>('/invoice-templates', data).then((r) => r.data),
  update: (id: number, data: Partial<InvoiceTemplate>) =>
    api.put<InvoiceTemplate>(`/invoice-templates/${id}`, data).then((r) => r.data),
  setDefault: (id: number) =>
    api.post<InvoiceTemplate>(`/invoice-templates/${id}/default`).then((r) => r.data),
  delete: (id: number) => api.delete(`/invoice-templates/${id}`).then((r) => r.data),
  /** Render a sample FACTURE using the in-flight (unsaved) template payload. */
  previewPdf: (data: Partial<InvoiceTemplate>, lang: 'fr' | 'ar' = 'fr') =>
    api.post('/invoice-templates/preview-pdf', data, {
      params: { lang },
      responseType: 'blob',
    }).then((r) => r.data as Blob),
};

// Document numbering configs (one per company × document type)
export const numberingConfigService = {
  getAll: () => api.get<NumberingConfig[]>('/numbering-config').then((r) => r.data),
  update: (id: number, data: Partial<NumberingConfig>) =>
    api.put<NumberingConfig>(`/numbering-config/${id}`, data).then((r) => r.data),
};

/**
 * Aligns the frontend payload with the backend DTO names. The form stores per-line
 * unit price as `prixUnitaireHT` and FODEC as a boolean `fodec`, but the backend
 * DTO ({@code LigneDocumentRequest}) expects `prixUnitaire` (NOT-NULL) and
 * `fodecApplicable`. Without this mapping the price is deserialized as null and
 * the create call NPEs server-side (500).
 */
function toBackendDocument(doc: Partial<Document>): any {
  if (!doc) return doc;
  const lignes = (doc.lignes ?? []).map((l: any) => {
    const { prixUnitaireHT, fodec, montantTva, ...rest } = l;
    return {
      ...rest,
      prixUnitaire: prixUnitaireHT,
      fodecApplicable: Boolean(fodec),
    };
  });
  return { ...doc, lignes };
}

/**
 * Reverse mapping for GET /documents responses. The backend response DTO uses
 * `prixUnitaire`, `designation`, `montantTVA`, and a BigDecimal `fodec` amount,
 * but the form/components bind to `prixUnitaireHT`, `articleDesignation`,
 * `montantTva`, and a boolean `fodec`. Without this, opening an invoice for
 * edit shows an empty unit-price column and the totals stay at 0 until the
 * user re-picks the article — which re-derives the missing fields client-side.
 */
function fromBackendDocument(d: any): Document {
  if (!d) return d;
  const lignes = (d.lignes ?? []).map((l: any) => ({
    ...l,
    prixUnitaireHT: l.prixUnitaire ?? l.prixUnitaireHT ?? 0,
    articleDesignation: l.designation ?? l.articleDesignation ?? '',
    montantTva: l.montantTVA ?? l.montantTva ?? 0,
    montantFodec: l.fodec ?? l.montantFodec ?? 0,
    fodec: typeof l.fodec === 'boolean'
      ? l.fodec
      : Number(l.fodec ?? 0) > 0,
    remise: l.remise ?? 0,
  }));
  // The backend response carries `netAPayer` and `montantPaye` but not
  // `resteAPayer`. The UI (payment form's invoice picker, document detail)
  // filters and displays on `resteAPayer`, so an undefined value silently
  // hides every unpaid invoice (`undefined > 0` is false). Derive it here.
  const netAPayer = Number(d.netAPayer ?? 0);
  const montantPaye = Number(d.montantPaye ?? 0);
  return {
    ...d,
    lignes,
    totalTva: d.totalTVA ?? d.totalTva ?? 0,
    totalFodec: d.fodec ?? d.totalFodec ?? 0,
    resteAPayer: d.resteAPayer ?? Math.max(0, netAPayer - montantPaye),
    // Pour un achat, le tiers est un fournisseur — on l'expose sous clientRaisonSociale
    // pour que les écrans existants (listes, détail) affichent le bon nom.
    clientRaisonSociale: d.clientRaisonSociale ?? d.fournisseurRaisonSociale,
  };
}

// Documents
export const documentService = {
  getAll: async (params?: DocumentSearchParams & { page?: number; size?: number; sens?: 'VENTE' | 'ACHAT' }) => {
    const type = (params as any)?.typeDocument;
    const sens = (params as any)?.sens;
    const url = type ? `/documents/type/${type}` : '/documents';
    const raw = (await api.get<any[]>(url, sens ? { params: { sens } } : undefined)).data;
    const all: Document[] = (raw ?? []).map(fromBackendDocument);
    let filtered: Document[] = all;
    if ((params as any)?.statutPaiement)
      filtered = filtered.filter((d: any) => d.statutPaiement === (params as any).statutPaiement);
    if ((params as any)?.clientId)
      filtered = filtered.filter((d: any) => d.clientId === (params as any).clientId);
    if (params?.search)
      filtered = applyTextFilter(filtered, params.search, ['numero', 'clientRaisonSociale', 'notes']);
    return paginateLocal(filtered, params);
  },

  getById: (id: number) => api.get<any>(`/documents/${id}`).then((r) => fromBackendDocument(r.data)),

  getByFournisseur: (fournisseurId: number) =>
    api.get<any[]>(`/documents/fournisseur/${fournisseurId}`)
      .then((r) => (r.data ?? []).map(fromBackendDocument)),

  create: (document: Partial<Document>) =>
    api.post<any>('/documents', toBackendDocument(document)).then((r) => fromBackendDocument(r.data)),

  update: (id: number, document: Partial<Document>) =>
    api.put<any>(`/documents/${id}`, toBackendDocument(document)).then((r) => fromBackendDocument(r.data)),

  delete: (id: number) => api.delete(`/documents/${id}`).then((r) => r.data),

  convertDevisToFacture: (devisId: number) =>
    api.post<Document>(`/documents/${devisId}/convert-to-facture`).then((r) => r.data),

  /** TF-002 — conversion en chaîne : devis → commande → bon de livraison → facture. */
  convertTo: (id: number, target: string) =>
    api.post<any>(`/documents/${id}/convert/${target}`).then((r) => fromBackendDocument(r.data)),

  /** TF-077 — crée une facture d'acompte (% du total HT) à partir d'un devis/commande. */
  creerAcompte: (id: number, pourcentage: number) =>
    api.post<any>(`/documents/${id}/acompte`, null, { params: { pourcentage } })
      .then((r) => fromBackendDocument(r.data)),

  /** TF-001 — fait évoluer le statut de cycle de vie métier d'un document. */
  changeStatut: (id: number, statut: string) =>
    api.patch<any>(`/documents/${id}/statut/${statut}`).then((r) => fromBackendDocument(r.data)),

  getPdf: (id: number, lang: 'fr' | 'ar' = 'fr') =>
    api.get(`/documents/${id}/pdf`, { params: { lang }, responseType: 'blob' }).then((r) => r.data),

  previewPdf: (document: Partial<Document>, lang: 'fr' | 'ar' = 'fr') =>
    api.post('/documents/preview-pdf', toBackendDocument(document), {
      params: { lang },
      responseType: 'blob',
    }).then((r) => r.data as Blob),

  // Facturation électronique (El Fatoora / TTN)
  getEInvoiceStatus: (id: number) =>
    api.get<FactureElectroniqueStatus>(`/documents/${id}/e-invoice`).then((r) => r.data),
  generateTeif: (id: number) =>
    api.post<FactureElectroniqueStatus>(`/documents/${id}/e-invoice/generate`).then((r) => r.data),
  submitToTtn: (id: number) =>
    api.post<FactureElectroniqueStatus>(`/documents/${id}/e-invoice/submit`).then((r) => r.data),
};

// Paiements
export const paiementService = {
  getAll: async (params?: PaiementSearchParams & { page?: number; size?: number }) => {
    const all = (await api.get<Paiement[]>('/paiements')).data;
    let filtered: Paiement[] = all;
    if ((params as any)?.modePaiement)
      filtered = filtered.filter((p: any) => p.modePaiement === (params as any).modePaiement);
    if ((params as any)?.dateDebut)
      filtered = filtered.filter((p: any) => p.datePaiement >= (params as any).dateDebut);
    if ((params as any)?.dateFin)
      filtered = filtered.filter((p: any) => p.datePaiement <= (params as any).dateFin);
    return paginateLocal(filtered, params);
  },

  getById: (id: number) => api.get<Paiement>(`/paiements/${id}`).then((r) => r.data),

  create: (paiement: Partial<Paiement>) =>
    api.post<Paiement>('/paiements', paiement).then((r) => r.data),

  getByDocument: (documentId: number) =>
    api.get<Paiement[]>(`/paiements/document/${documentId}`).then((r) => r.data),

  delete: (id: number) => api.delete(`/paiements/${id}`).then((r) => r.data),
};

// Échéancier de paiement (plan en N fois) — sous-ressource d'un document
export const echeancierService = {
  get: (documentId: number) =>
    api.get<Echeancier>(`/documents/${documentId}/echeancier`).then((r) => r.data),

  replace: (
    documentId: number,
    echeances: Array<{ libelle?: string; dateEcheance: string; montant: number }>,
  ) =>
    api
      .put<Echeancier>(`/documents/${documentId}/echeancier`, { echeances })
      .then((r) => r.data),

  generate: (
    documentId: number,
    params: { nombre: number; premiereEcheance: string; intervalleJours: number },
  ) =>
    api
      .post<Echeancier>(`/documents/${documentId}/echeancier/generate`, params)
      .then((r) => r.data),

  delete: (documentId: number) =>
    api.delete(`/documents/${documentId}/echeancier`).then((r) => r.data),
};

// Company — new backend exposes /companies and returns the list of accessible companies.
export const companyService = {
  get: async (): Promise<Company> => {
    const cid = auth.getCompanyId();
    const list = (await api.get<Company[]>('/companies')).data || [];
    return list.find((c: any) => String(c.id) === cid) || list[0];
  },

  update: async (company: Partial<Company>): Promise<Company> => {
    const current = await companyService.get();
    if (!current) throw new Error('No active company');
    return api.put<Company>(`/companies/${(current as any).id}`, company).then((r) => r.data);
  },

  uploadLogo: async (file: File): Promise<{ url: string }> => {
    const current = await companyService.get();
    if (!current) throw new Error('No active company');
    const form = new FormData();
    form.append('file', file);
    const resp = await api.post<Company>(`/companies/${(current as any).id}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { url: (resp.data as any).logoUrl || '' };
  },
};

// 2FA TOTP
export const totpService = {
  setup: () => api.post<{ secret: string; otpauthUrl: string; qrDataUri: string }>('/auth/2fa/setup').then((r) => r.data),
  verify: (code: string) => api.post('/auth/2fa/verify', { code }).then(() => true),
  disable: (code: string) => api.post('/auth/2fa/disable', { code }).then(() => true),
};

// API keys
export interface ApiKeyView {
  id: number; name: string; keyPrefix: string; scopes?: string;
  revoked: boolean; lastUsedAt?: string; createdAt: string; plainKey?: string;
}
export const apiKeyService = {
  list: () => api.get<ApiKeyView[]>('/api-keys').then((r) => r.data),
  create: (name: string, scopes?: string) =>
    api.post<ApiKeyView>('/api-keys', { name, scopes }).then((r) => r.data),
  revoke: (id: number) => api.delete(`/api-keys/${id}`).then(() => true),
};

// Plan
export const planService = {
  update: async (plan: 'STARTER' | 'PRO' | 'BUSINESS'): Promise<Company> => {
    const current = await companyService.get();
    if (!current) throw new Error('No active company');
    return api.put<Company>(`/companies/${(current as any).id}/plan`, { plan }).then((r) => r.data);
  },
};

// GDPR
export const gdprService = {
  exportZip: () => api.get('/gdpr/export', { responseType: 'blob' }).then((r) => r.data as Blob),
  anonymizeMe: () => api.post('/gdpr/anonymize-me').then(() => true),
  anonymizeClient: (clientId: number) => api.post(`/gdpr/anonymize-client/${clientId}`).then(() => true),
};

// Audit log
export interface AuditLogEntry {
  id: number; companyId?: number; utilisateurId?: number;
  action: string; entityType?: string; entityId?: string; details?: string;
  correlationId?: string; ipAddress?: string; createdAt: string;
}
export const auditLogService = {
  list: (page = 0, size = 50) =>
    api.get<{ content: AuditLogEntry[]; totalElements: number; totalPages: number; number: number }>(
      '/audit-logs', { params: { page, size } },
    ).then((r) => r.data),
};

// Export comptable
export const exportComptableService = {
  export: (startDate: string, endDate: string, format: 'CIEL_CSV' | 'SAGE_PNM' | 'FEC') =>
    api.post('/export/comptable', { startDate, endDate, format }, { responseType: 'blob' })
      .then((r) => r.data as Blob),
  // TF-074 — export Excel généralisé (CSV ouvrable dans Excel)
  exportExcel: (type: 'clients' | 'articles' | 'factures' | 'paiements') =>
    api.get(`/export/excel/${type}`, { responseType: 'blob' }).then((r) => r.data as Blob),
};

// TF-080 — webhooks sortants (API v2)
export interface WebhookEndpointView {
  id: number;
  url: string;
  events: string;
  actif: boolean;
  description?: string | null;
  createdAt: string;
}
export interface WebhookEndpointCreated {
  endpoint: WebhookEndpointView;
  secret: string;
}
// TF-055 — back-office des dépôts de facture fournisseur
export const depotFactureService = {
  list: () => api.get<import('./portal').DepotFacture[]>('/achats/depots-factures').then((r) => r.data),
  fichier: (id: number) =>
    api.get(`/achats/depots-factures/${id}/fichier`, { responseType: 'blob' }).then((r) => r.data as Blob),
  traiter: (id: number, note?: string) =>
    api.post(`/achats/depots-factures/${id}/traiter`, { note }).then((r) => r.data),
  rejeter: (id: number, note?: string) =>
    api.post(`/achats/depots-factures/${id}/rejeter`, { note }).then((r) => r.data),
};

export const webhookService = {
  list: () => api.get<WebhookEndpointView[]>('/webhook-endpoints').then((r) => r.data),
  events: () => api.get<string[]>('/webhook-endpoints/events').then((r) => r.data),
  create: (url: string, events: string, description?: string) =>
    api.post<WebhookEndpointCreated>('/webhook-endpoints', { url, events, description }).then((r) => r.data),
  update: (id: number, patch: Partial<{ url: string; events: string; description: string; actif: boolean }>) =>
    api.put<WebhookEndpointView>(`/webhook-endpoints/${id}`, patch).then((r) => r.data),
  remove: (id: number) => api.delete(`/webhook-endpoints/${id}`).then((r) => r.data),
};

// Payment links
export interface PaymentLinkView {
  id: number; documentId: number; documentNumero: string;
  provider: 'KONNECT' | 'CLICTOPAY' | 'FLOUCI';
  externalId: string; url: string; amount: number;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'EXPIRED';
  createdAt: string; paidAt?: string;
}
export const paymentLinkService = {
  create: (documentId: number, provider: PaymentLinkView['provider'], sendEmail = false, customerEmail?: string) =>
    api.post<PaymentLinkView>('/payment-links', { documentId, provider, sendEmail, customerEmail }).then((r) => r.data),
  listForDocument: (documentId: number) =>
    api.get<PaymentLinkView[]>(`/payment-links/document/${documentId}`).then((r) => r.data),
  refund: (id: number, amount?: number, reason?: string) =>
    api.post<PaymentLinkView>(`/payment-links/${id}/refund`, { amount, reason }).then((r) => r.data),
};

// Signature keystore (PDF-10)
export const signatureService = {
  upload: async (file: File, password: string, tsaUrl?: string): Promise<Company> => {
    const current = await companyService.get();
    if (!current) throw new Error('No active company');
    const form = new FormData();
    form.append('file', file);
    form.append('password', password);
    if (tsaUrl) form.append('tsaUrl', tsaUrl);
    return api.post<Company>(`/companies/${(current as any).id}/signature-keystore`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
  remove: async (): Promise<Company> => {
    const current = await companyService.get();
    if (!current) throw new Error('No active company');
    return api.delete<Company>(`/companies/${(current as any).id}/signature-keystore`).then((r) => r.data);
  },
};

// Stock movements
export type OrigineMouvement = 'DOCUMENT' | 'VENTE' | 'ACHAT' | 'AJUSTEMENT' | 'INVENTAIRE' | 'TRANSFERT';
export interface MouvementStockView {
  id: number; articleId: number; articleReference: string; articleDesignation: string;
  documentId?: number; documentNumero?: string;
  sens: 'IN' | 'OUT'; origine?: OrigineMouvement;
  quantite: number; coutUnitaire?: number;
  depotId?: number | null; depotNom?: string | null;
  numeroLot?: string | null; numeroSerie?: string | null; dlc?: string | null;
  motif?: string; createdAt: string;
}
export interface MouvementManuelPayload {
  articleId: number; sens: 'IN' | 'OUT'; quantite: number; coutUnitaire?: number; motif?: string;
  depotId?: number | null; numeroLot?: string; numeroSerie?: string; dlc?: string;
}
export interface InventairePayload {
  articleId: number; stockReel: number; motif?: string;
}
export const stockHistoryService = {
  history: () => api.get<MouvementStockView[]>('/stock/mouvements').then((r) => r.data),
  forArticle: (articleId: number) =>
    api.get<MouvementStockView[]>(`/stock/mouvements/article/${articleId}`).then((r) => r.data),
  rupture: () => api.get<any[]>('/articles/rupture').then((r) => r.data),
  createMovement: (payload: MouvementManuelPayload) =>
    api.post<MouvementStockView>('/stock/mouvements', payload).then((r) => r.data),
  createInventaire: (payload: InventairePayload) =>
    api.post<MouvementStockView>('/stock/inventaire', payload).then((r) => r.data),
};

// TF-070 — multi-dépôt + transferts ; TF-071 — lots/séries/DLC
export interface DepotView {
  id: number; code: string; nom: string; adresse?: string | null; actif: boolean; createdAt: string;
}
export interface StockDepotLigne {
  articleId: number; reference: string; designation: string;
  depotId: number | null; depotNom: string; quantite: number;
}
export interface LotLigne {
  articleId: number; reference: string; designation: string; numeroLot: string;
  dlc?: string | null; solde: number; perime: boolean; dlcProche: boolean;
}
export const depotService = {
  list: () => api.get<DepotView[]>('/stock/depots').then((r) => r.data),
  create: (payload: { code: string; nom: string; adresse?: string }) =>
    api.post<DepotView>('/stock/depots', payload).then((r) => r.data),
  update: (id: number, patch: Partial<{ nom: string; adresse: string; actif: boolean }>) =>
    api.put<DepotView>(`/stock/depots/${id}`, patch).then((r) => r.data),
  remove: (id: number) => api.delete(`/stock/depots/${id}`).then((r) => r.data),
  stockParDepot: () => api.get<StockDepotLigne[]>('/stock/par-depot').then((r) => r.data),
  transferer: (payload: {
    articleId: number; depotSourceId: number | null; depotDestinationId: number | null;
    quantite: number; numeroLot?: string; motif?: string;
  }) => api.post<MouvementStockView[]>('/stock/transferts', payload).then((r) => r.data),
  lots: (joursAlerteDlc = 30) =>
    api.get<LotLigne[]>('/stock/lots', { params: { joursAlerteDlc } }).then((r) => r.data),
  mouvementsLot: (numeroLot: string) =>
    api.get<MouvementStockView[]>(`/stock/lots/${encodeURIComponent(numeroLot)}/mouvements`).then((r) => r.data),
};

// Réappro intelligent — suggestions de réapprovisionnement + bons de commande
export interface ReapproSuggestion {
  articleId: number; reference: string; designation: string;
  stockActuel: number; stockMinimum: number;
  avgDailyDemand: number; leadTimeDays: number;
  safetyStock: number; reorderPoint: number;
  suggestedQty: number; cmup: number;
  fournisseurId?: number; fournisseurNom?: string;
  rationale?: string; source: 'STAT' | 'AI';
}
export interface BonCommandePayload {
  fournisseurId: number;
  lignes: { articleId: number; quantite: number; prixUnitaire?: number }[];
}
export interface BonsCommandeAutoResult {
  bonsCommande: any[];
  articlesSansFournisseur: number[];
}
export const reapproService = {
  suggestions: (useAi = false) =>
    api.get<ReapproSuggestion[]>('/stock/reappro/suggestions', { params: { useAi } }).then((r) => r.data),
  generateBonCommande: (payload: BonCommandePayload) =>
    api.post('/stock/reappro/bon-commande', payload).then((r) => r.data),
  generateBonsCommandeAuto: (lignes: { articleId: number; quantite: number }[]) =>
    api.post<BonsCommandeAutoResult>('/stock/reappro/bons-commande', { lignes }).then((r) => r.data),
};

// Catalogue article-fournisseur
export interface ArticleFournisseur {
  id: number; articleId: number; articleReference?: string; articleDesignation?: string;
  fournisseurId: number; fournisseurRaisonSociale?: string;
  referenceFournisseur?: string; prixAchat?: number; delaiJours?: number;
  conditionnement?: number; prefere: boolean;
}
export const articleFournisseurService = {
  list: (articleId: number) =>
    api.get<ArticleFournisseur[]>(`/articles/${articleId}/fournisseurs`).then((r) => r.data),
  create: (articleId: number, data: Partial<ArticleFournisseur>) =>
    api.post<ArticleFournisseur>(`/articles/${articleId}/fournisseurs`, data).then((r) => r.data),
  update: (articleId: number, id: number, data: Partial<ArticleFournisseur>) =>
    api.put<ArticleFournisseur>(`/articles/${articleId}/fournisseurs/${id}`, data).then((r) => r.data),
  delete: (articleId: number, id: number) =>
    api.delete(`/articles/${articleId}/fournisseurs/${id}`).then((r) => r.data),
};

// Analytique du stock — valorisation, rotation, ABC, stock dormant
export interface StockAnalytics {
  valorisationTotale: number;
  totalArticles: number;
  windowDays: number;
  dormantDays: number;
  coutMarchandisesVendues: number;
  rotation: number;
  valorisationParCategorie: { categorie: string; valeur: number }[];
  abc: {
    articleId: number; reference: string; designation: string;
    valeurConsommee: number; partPct: number; cumulPct: number; classe: 'A' | 'B' | 'C';
  }[];
  dormant: {
    articleId: number; reference: string; designation: string;
    stockActuel: number; valeur: number; joursSansMouvement: number | null;
  }[];
}
export const stockAnalyticsService = {
  get: (windowDays = 90, dormantDays = 90) =>
    api.get<StockAnalytics>('/stock/analytics', { params: { windowDays, dormantDays } }).then((r) => r.data),
};

// OCR — extraction de factures fournisseurs (pré-remplissage)
export interface OcrExtractResult {
  supplier?: string; number?: string; date?: string;
  totalHT?: number; totalTVA?: number; totalTTC?: number;
  confidence?: Record<string, number>;
}
export const ocrService = {
  extract: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ provider: string; usedFallback?: boolean; fields: OcrExtractResult }>('/ocr/extract', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data);
  },
};

// Rapports — backend endpoints live under /dashboard. We assemble the full shape
// expected by the UI from the plain endpoints the backend exposes today.
export const rapportService = {
  getTVA: async (annee: number, mois: number): Promise<RapportTVAData> => {
    const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    // Le backend renvoie tva7/totalTVA/totalFodec… : on les ré-aligne sur la forme attendue par l'UI.
    const d: any = (await api.get('/dashboard/rapport-tva', { params: { annee, mois } })).data;
    return {
      periode: `${MONTHS[((d.mois ?? mois) - 1)] ?? d.mois} ${d.annee ?? annee}`,
      tva7Collectee: Number(d.tva7 ?? 0),
      tva13Collectee: Number(d.tva13 ?? 0),
      tva19Collectee: Number(d.tva19 ?? 0),
      totalTvaCollectee: Number(d.totalTVA ?? 0),
      baseHT7: Number(d.baseHT7 ?? 0),
      baseHT13: Number(d.baseHT13 ?? 0),
      baseHT19: Number(d.baseHT19 ?? 0),
      totalBaseHT: Number(d.totalBaseHT ?? 0),
      fodecTotal: Number(d.totalFodec ?? 0),
      timbreTotal: Number(d.totalTimbre ?? 0),
      tvaDeductible: Number(d.tvaDeductible ?? 0),
      tvaNetteAPayer: Number(d.tvaNetteAPayer ?? 0),
    };
  },

  /** TF-011 — rapport de marge par produit ou client sur une période. */
  getMarge: async (
    startDate: string,
    endDate: string,
    groupBy: 'produit' | 'client' = 'produit',
  ): Promise<MargeRapport> =>
    (await api.get<MargeRapport>('/reporting/marge', {
      params: { startDate, endDate, groupBy },
    })).data,

  getEtatAnnuel: async (annee: number): Promise<EtatAnnuelData> => {
    const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin',
                          'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

    const [caMensuelRaw, allDocs] = await Promise.all([
      api.get<any[]>('/dashboard/ca-mensuel', { params: { annee } }).then((r) => r.data || []),
      api.get<any[]>('/documents').then((r) => r.data || []),
    ]);

    // Monthly chart data: map {mois, annee, totalHT} → {mois: "Jan", montant: …}
    const caMensuel = caMensuelRaw.map((m: any) => ({
      mois: MONTH_LABELS[(m.mois || 1) - 1] || String(m.mois),
      montant: Number(m.totalHT || 0),
    }));

    // Filter docs to the requested year, ventes uniquement (exclut les achats fournisseurs).
    const docsAnnee = allDocs.filter(
      (d: any) => typeof d.dateDocument === 'string' && d.dateDocument.startsWith(String(annee))
        && (d.sensDocument ?? 'VENTE') === 'VENTE',
    );

    const factures = docsAnnee.filter((d: any) => d.typeDocument === 'FACTURE');
    const devis = docsAnnee.filter((d: any) => d.typeDocument === 'DEVIS');
    const avoirs = docsAnnee.filter((d: any) => d.typeDocument === 'AVOIR');

    const caTotal = factures.reduce((s: number, d: any) => s + Number(d.totalHT || 0), 0);
    const tvaCollectee = factures.reduce((s: number, d: any) => s + Number(d.totalTVA || 0), 0);

    // Répartition par taux TVA (en se basant sur les lignes)
    const byRate: Record<string, number> = {};
    for (const d of factures) {
      for (const l of d.lignes || []) {
        const rate = l.tauxTva || 'TVA_0';
        byRate[rate] = (byRate[rate] || 0) + Number(l.montantTVA || 0);
      }
    }
    const repartitionTva = Object.entries(byRate)
      .filter(([, montant]) => montant > 0)
      .map(([tauxTva, montant]) => ({ tauxTva, montant }));

    // Top clients par CA HT cette année.
    const byClient: Record<string, { clientId: number; raisonSociale: string; totalCA: number; nombreFactures: number }> = {};
    for (const f of factures) {
      const key = String(f.clientId);
      if (!byClient[key]) {
        byClient[key] = {
          clientId: f.clientId, raisonSociale: f.clientRaisonSociale,
          totalCA: 0, nombreFactures: 0,
        };
      }
      byClient[key].totalCA += Number(f.totalHT || 0);
      byClient[key].nombreFactures += 1;
    }
    const topClients = Object.values(byClient)
      .sort((a, b) => b.totalCA - a.totalCA)
      .slice(0, 10);

    return {
      annee,
      caTotal,
      caMensuel,
      tvaCollectee,
      nombreFactures: factures.length,
      nombreDevis: devis.length,
      nombreAvoirs: avoirs.length,
      topClients,
      repartitionTva,
    };
  },
};

// Opportunités / pipeline commercial (TF-005)
export const opportuniteService = {
  getPipeline: () => api.get<Pipeline>('/opportunites/pipeline').then((r) => r.data),
  getAll: () => api.get<Opportunite[]>('/opportunites').then((r) => r.data ?? []),
  getById: (id: number) => api.get<Opportunite>(`/opportunites/${id}`).then((r) => r.data),
  getByClient: (clientId: number) =>
    api.get<Opportunite[]>(`/opportunites/client/${clientId}`).then((r) => r.data ?? []),
  create: (o: Partial<Opportunite>) =>
    api.post<Opportunite>('/opportunites', o).then((r) => r.data),
  update: (id: number, o: Partial<Opportunite>) =>
    api.put<Opportunite>(`/opportunites/${id}`, o).then((r) => r.data),
  changeEtape: (id: number, etape: string, motifPerte?: string) =>
    api.patch<Opportunite>(`/opportunites/${id}/etape/${etape}`,
      motifPerte ? { motifPerte } : {}).then((r) => r.data),
  delete: (id: number) => api.delete(`/opportunites/${id}`).then((r) => r.data),
};

// Contacts multiples d'un tiers (TF-004)
export const contactService = {
  getByClient: (clientId: number) =>
    api.get<Contact[]>(`/contacts/client/${clientId}`).then((r) => r.data ?? []),
  getByFournisseur: (fournisseurId: number) =>
    api.get<Contact[]>(`/contacts/fournisseur/${fournisseurId}`).then((r) => r.data ?? []),
  create: (c: Partial<Contact>) => api.post<Contact>('/contacts', c).then((r) => r.data),
  update: (id: number, c: Partial<Contact>) =>
    api.put<Contact>(`/contacts/${id}`, c).then((r) => r.data),
  delete: (id: number) => api.delete(`/contacts/${id}`).then((r) => r.data),
};

// Moteur d'alertes (TF-019)
export const alerteService = {
  getAll: () => api.get<Alerte[]>('/alertes').then((r) => r.data ?? []),
};

// Recouvrement — balance âgée des impayés (TF-010)
export const recouvrementService = {
  getBalanceAgee: () => api.get<BalanceAgee>('/reporting/balance-agee').then((r) => r.data),
  getTresorerie: (mois = 6, soldeOuverture = 0) =>
    api.get<Tresorerie>('/reporting/tresorerie', { params: { mois, soldeOuverture } }).then((r) => r.data),
  setLitige: (documentId: number, enLitige: boolean, motif?: string) =>
    api.patch(`/documents/${documentId}/litige`, motif ? { motif } : {},
      { params: { enLitige } }).then((r) => r.data),
};

// Objectifs / budgets annuels (TF-075)
export const objectifService = {
  get: (annee?: number) =>
    api.get<import('../types').ObjectifAnnuel>('/objectifs', { params: { annee } }).then((r) => r.data),
  upsert: (o: import('../types').ObjectifAnnuel) =>
    api.put<import('../types').ObjectifAnnuel>('/objectifs', o).then((r) => r.data),
};

// Activités CRM — timeline (TF-078)
export const activiteService = {
  getByClient: (clientId: number) =>
    api.get<import('../types').Activite[]>(`/activites/client/${clientId}`).then((r) => r.data ?? []),
  create: (a: Partial<import('../types').Activite>) =>
    api.post<import('../types').Activite>('/activites', a).then((r) => r.data),
  update: (id: number, a: Partial<import('../types').Activite>) =>
    api.put<import('../types').Activite>(`/activites/${id}`, a).then((r) => r.data),
  delete: (id: number) => api.delete(`/activites/${id}`).then((r) => r.data),
};

// Recouvrement — paliers de relance (TF-056) & promesses de paiement (TF-057)
export const recouvrementConfigService = {
  getPaliers: () =>
    api.get<import('../types').RelancePalier[]>('/recouvrement/paliers').then((r) => r.data ?? []),
  createPalier: (p: Partial<import('../types').RelancePalier>) =>
    api.post<import('../types').RelancePalier>('/recouvrement/paliers', p).then((r) => r.data),
  deletePalier: (id: number) => api.delete(`/recouvrement/paliers/${id}`).then((r) => r.data),
  getPromesses: () =>
    api.get<import('../types').PromessePaiement[]>('/recouvrement/promesses').then((r) => r.data ?? []),
  createPromesse: (p: { documentId: number; datePromesse: string; montant?: number; note?: string }) =>
    api.post<import('../types').PromessePaiement>('/recouvrement/promesses', p).then((r) => r.data),
  updatePromesseStatut: (id: number, statut: string) =>
    api.patch<import('../types').PromessePaiement>(`/recouvrement/promesses/${id}/statut`, { statut })
      .then((r) => r.data),
  deletePromesse: (id: number) => api.delete(`/recouvrement/promesses/${id}`).then((r) => r.data),
};

// Portail client — gestion des accès côté back-office (TF-042)
export const portalAdminService = {
  generateToken: (clientId: number) =>
    api.post<import('../types').PortalTokenInfo>(`/portal/clients/${clientId}/token`).then((r) => r.data),
  listTokens: (clientId: number) =>
    api.get<import('../types').PortalTokenInfo[]>(`/portal/clients/${clientId}/tokens`).then((r) => r.data ?? []),
  revoke: (id: number) => api.delete(`/portal/tokens/${id}`).then((r) => r.data),
};

// Portail fournisseur — gestion des accès côté back-office (TF-052)
export const supplierPortalAdminService = {
  generateToken: (fournisseurId: number) =>
    api.post<import('../types').PortalTokenInfo>(`/supplier-portal/fournisseurs/${fournisseurId}/token`)
      .then((r) => r.data),
  revoke: (id: number) => api.delete(`/supplier-portal/tokens/${id}`).then((r) => r.data),
};

// Gouvernance carbone (TF-035 → TF-038)
export const carbonGovernanceService = {
  getSummary: () =>
    api.get<import('../types').CarbonGovernanceSummary>('/carbone/gouvernance/summary').then((r) => r.data),
  getArticlesACompleter: () =>
    api.get<import('../types').ArticleACompleter[]>('/carbone/gouvernance/articles-a-completer')
      .then((r) => r.data ?? []),
  getCategoryFactors: () =>
    api.get<import('../types').CarbonCategoryFactor[]>('/carbone/gouvernance/category-factors')
      .then((r) => r.data ?? []),
  upsertFactor: (categorie: string, facteurMonetaire: number) =>
    api.put<import('../types').CarbonCategoryFactor>('/carbone/gouvernance/category-factors',
      { categorie, facteurMonetaire }).then((r) => r.data),
  deleteFactor: (id: number) =>
    api.delete(`/carbone/gouvernance/category-factors/${id}`).then((r) => r.data),
};

// Moteur de validations / approbations (TF-047 → TF-051)
export const workflowService = {
  getRules: () =>
    api.get<import('../types').ApprovalRule[]>('/workflow/rules').then((r) => r.data ?? []),
  createRule: (rule: Partial<import('../types').ApprovalRule>) =>
    api.post<import('../types').ApprovalRule>('/workflow/rules', rule).then((r) => r.data),
  updateRule: (id: number, rule: Partial<import('../types').ApprovalRule>) =>
    api.put<import('../types').ApprovalRule>(`/workflow/rules/${id}`, rule).then((r) => r.data),
  deleteRule: (id: number) => api.delete(`/workflow/rules/${id}`).then((r) => r.data),
  getRequests: (statut?: import('../types').ApprovalStatut) =>
    api.get<import('../types').ApprovalRequest[]>('/workflow/requests', { params: { statut } })
      .then((r) => r.data ?? []),
  getSummary: () =>
    api.get<import('../types').WorkflowSummary>('/workflow/requests/summary').then((r) => r.data),
  approve: (id: number, motif?: string) =>
    api.post(`/workflow/requests/${id}/approve`, { motif }).then((r) => r.data),
  reject: (id: number, motif?: string) =>
    api.post(`/workflow/requests/${id}/reject`, { motif }).then((r) => r.data),
};

// Fiscalité — contrôle TVA (TF-039) et calendrier fiscal (TF-040)
export const fiscaliteService = {
  getControleTva: (mois?: number, annee?: number) =>
    api.get<import('../types').ControleTva>('/fiscalite/controle-tva', { params: { mois, annee } })
      .then((r) => r.data),
  getCalendrier: (moisAVenir = 6) =>
    api.get<import('../types').EcheanceFiscale[]>('/fiscalite/calendrier', { params: { moisAVenir } })
      .then((r) => r.data ?? []),
  getRetenues: (annee?: number) =>
    api.get<import('../types').EtatRetenues>('/fiscalite/retenues', { params: { annee } }).then((r) => r.data),
};

// Qualité de données — scanner d'anomalies (TF-029 → TF-034)
export const dataQualityService = {
  getReport: () =>
    api.get<import('../types').DataQualityReport>('/data-quality').then((r) => r.data),
  getSummary: () =>
    api.get<import('../types').DataQualitySummary>('/data-quality/summary').then((r) => r.data),
};

// Cockpit carbone (TF-021/022)
export const carboneReportingService = {
  getCockpit: (startDate: string, endDate: string) =>
    api.get<CockpitCarbone>('/reporting/carbone', { params: { startDate, endDate } }).then((r) => r.data),
  getRecommandations: () =>
    api.get<import('../types').RecommandationCarbone[]>('/reporting/carbone/recommandations').then((r) => r.data ?? []),
};

// Grilles tarifaires & remises client (TF-024)
export const tarifService = {
  getByClient: (clientId: number) =>
    api.get<import('../types').TarifClient[]>(`/tarifs/client/${clientId}`).then((r) => r.data ?? []),
  create: (t: Partial<import('../types').TarifClient>) =>
    api.post<import('../types').TarifClient>('/tarifs', t).then((r) => r.data),
  delete: (id: number) => api.delete(`/tarifs/${id}`).then((r) => r.data),
  resolve: (clientId: number, articleId?: number) =>
    api.get('/tarifs/resolve', { params: { clientId, articleId } }).then((r) => r.data),
};

// Rapprochement bancaire assisté (TF-006)
export const rapprochementAssisteService = {
  rapprocher: (csv: string) =>
    api.post<import('../types').RapprochementAssiste>('/comptabilite/rapprochement-assiste', { csv })
      .then((r) => r.data),
};

// Assistant IA dirigeant (TF-027)
export const assistantService = {
  syntheseDirigeant: () =>
    api.get<import('../types').AssistantSynthese>('/assistant/dirigeant').then((r) => r.data),
  suggererDevis: (demande: string) =>
    api.post<import('../types').AssistantDevis>('/assistant/devis', { demande }).then((r) => r.data),
  // TF-058 — brouillon de relance pour une facture impayée
  genererRelance: (documentId: number, palier?: number) =>
    api.post<RelanceBrouillon>('/assistant/relance', { documentId, palier }).then((r) => r.data),
  // TF-081 — actions chiffrées de réduction CO₂ sans perte de marge
  actionsCarbone: () =>
    api.get<AssistantCarbone>('/assistant/carbone').then((r) => r.data),
  // TF-069 — envoie le rapport de pilotage mensuel par e-mail (ADMIN)
  envoyerRapportMensuel: () =>
    api.post<import('../types').AssistantSynthese>('/assistant/dirigeant/rapport-mensuel').then((r) => r.data),
};

export interface AssistantCarboneAction {
  type: string;
  priorite: string;
  titre: string;
  detail: string;
  articleId?: number | null;
  gainIntensite?: number | null;
  impactMargePct?: number | null;
}

export interface AssistantCarbone {
  synthese: string;
  genereParIa: boolean;
  actions: AssistantCarboneAction[];
}

export interface RelanceBrouillon {
  sujet: string;
  corps: string;
  palier: number;
  genereParIa: boolean;
}

// Valorisation du stock (TF-025)
export const stockValorisationService = {
  get: () => api.get<import('../types').StockValorisation>('/stock/valorisation').then((r) => r.data),
};
